/* eslint-disable react/jsx-filename-extension */
import React, { useMemo, useEffect, useRef, forwardRef } from 'react'
import { useSelector } from 'react-redux'
import {
    useLocation,
    useNavigate,
    useParams,
    matchPath as v6MatchPath,
    Routes,
    Route as V6Route,
    Navigate,
    Link as RouterLink,
    NavLink as RouterNavLink
} from 'react-router'
import { history as historySingleton } from '../../redux/history'

const noopUnsubscribe = () => {}

const locationListeners = new Set()

// Ordered list of navigation blockers registered by <ReactRouterPause>. Each
// entry stays registered for the lifetime of its component (mount→unmount) and
// reports whether it currently wants to block via `isActive()`, which reads the
// live `when` prop. Registration is deliberately NOT tied to `when`: a blocker's
// `when` can flap true↔false across data-driven re-renders, and tying
// register/unregister to it opened windows where a navigation slipped through
// unblocked (the source of the intermittent discard prompt). The active blocker
// is resolved per-navigation instead. Last-registered active blocker wins so the
// innermost blocker (e.g. a dialog mounted on top of a builder) decides first.
const blockerRegistry = []

let isBypassingBlockers = false

// True while a blocker handler is executing. Handlers commonly dispatch into
// redux-saga flows that navigate synchronously (e.g. ConnectionBuilder's
// verifyChangedConn -> onCheckChanges -> onDiscard -> history.navigate). That
// nested navigation must NOT re-consult the blocker: React hasn't re-rendered
// yet, so the blocker's `when` ref still reads true and the handler would run
// again, dispatch again, navigate again — an infinite synchronous loop that
// freezes the app. Mirrors @allpro/react-router-pause, where navigations issued
// from inside a handler (navigation.push/replace) always bypassed the pause.
let isConsultingBlocker = false

/**
 * Invoke a blocker handler with the re-entrancy guard set, so any navigation
 * triggered synchronously from inside the handler (directly or via a dispatched
 * saga) bypasses blocker consultation instead of looping back into it.
 *
 * @param {object} entry - The blocker registry entry.
 * @param {object} navigation - Navigation control object for the handler.
 * @param {object} target - v5-shaped target location.
 * @param {string} action - 'PUSH'|'REPLACE'|'POP'.
 * @returns {*} The handler's return value.
 */
const consultBlocker = (entry, navigation, target, action) => {
    isConsultingBlocker = true
    try {
        return entry.handler(navigation, target, action)
    } finally {
        isConsultingBlocker = false
    }
}

/**
 * Resolve the blocker that should handle the current navigation: the
 * most-recently-registered entry whose `isActive()` is true. Returns undefined
 * when no blocker currently wants to block.
 *
 * @returns {object|undefined} The active blocker entry, or undefined.
 */
const findActiveBlocker = () => [...blockerRegistry].reverse().find(b => b.isActive && b.isActive())

/**
 * Normalize a v5-style path to a list of v6-safe paths to try (in order).
 *
 *   `:foo*` -> [`:foo`, `:foo/*`, `<no :foo segment>`]
 *      v5 `:foo*` is "0 or more" — matches when the segment is absent, present
 *      with one segment, or present with extra path. v6 has no equivalent so
 *      we emit three candidates. Try order matters: the segment-capturing
 *      forms are tried first so callers reading `params.foo` get it when the
 *      segment is present; absent is the last-resort fallback for paths that
 *      omit the segment entirely.
 *
 *   `:foo?` -> [`:foo`, `<no :foo segment>`]
 *      v5 optional segment — emit the with-segment form first, then absent.
 *
 * @param {string} path - A possibly v5-style path.
 * @returns {string[]} A list of v6-safe path patterns.
 */
const expandV5Path = (path) => {
    if (typeof path !== 'string') return [path]
    let candidates = [path]
    // v5 `:foo*` -> emit `:foo`, `:foo/*`, and the absent variant.
    if (/\/:\w+\*/.test(path)) {
        candidates = [
            path.replace(/(:\w+)\*/g, '$1'),     // present (one segment)
            path.replace(/(:\w+)\*/g, '$1/*'),   // present + trailing
            path.replace(/\/:\w+\*/g, '')        // absent (last-resort)
        ]
    }
    // v5 `:foo?` -> emit the with-segment form first, then absent.
    candidates = candidates.flatMap(p => {
        if (!/\/:\w+\?/.test(p)) return [p]
        return [
            p.replace(/(:\w+)\?/g, '$1'),
            p.replace(/\/:\w+\?/g, '')
        ]
    })
    // De-duplicate while preserving order.
    return [...new Set(candidates)]
}

const buildHistoryShim = (navigate, location) => ({
    push: (to, state) => navigate(to, state ? { state } : undefined),
    replace: (to, state) => navigate(to, { replace: true, ...(state ? { state } : {}) }),
    goBack: () => navigate(-1),
    goForward: () => navigate(1),
    go: (n) => navigate(n),
    listen: (listener) => {
        locationListeners.add(listener)
        return () => { locationListeners.delete(listener) }
    },
    block: () => noopUnsubscribe,
    location,
    action: 'POP',
    length: 0
})

/**
 * Build a v5-shaped history object whose identity is stable across re-renders.
 * v5's `history` was a singleton; in v6 we synthesize one per render. To keep
 * `useHistory()` results stable (so `[history]` deps don't refire on every
 * navigation), the methods read from refs and `location` is exposed via a
 * getter. The resulting object passes `===` across renders for the same hook
 * call site while still reflecting the current navigate/location on each access.
 *
 * @param {object} navigateRef - A ref whose `.current` holds the latest navigate.
 * @param {object} locationRef - A ref whose `.current` holds the latest location.
 * @returns {object} v5-shaped history object with stable identity.
 */
const buildStableHistoryShim = (navigateRef, locationRef) => {
    const shim = {
        push: (to, state) => navigateRef.current(to, state ? { state } : undefined),
        replace: (to, state) => navigateRef.current(to, { replace: true, ...(state ? { state } : {}) }),
        goBack: () => navigateRef.current(-1),
        goForward: () => navigateRef.current(1),
        go: (n) => navigateRef.current(n),
        listen: (listener) => {
            locationListeners.add(listener)
            return () => { locationListeners.delete(listener) }
        },
        block: () => noopUnsubscribe,
        action: 'POP',
        length: 0
    }
    Object.defineProperty(shim, 'location', {
        enumerable: true,
        configurable: false,
        get: () => locationRef.current
    })
    return shim
}

/**
 * Decide whether a navigation target is safe to hand to react-router.
 *
 * SG-14529 / CVE-2026-53669 — defence in depth over react-router 7's own
 * `validateNavigationTarget`, which throws on an external destination. This fails
 * soft instead (warn, no navigation), and it is the ONLY control on the compat
 * `Link`/`NavLink` path: react-router renders a backslash-shaped `to` as an
 * off-origin `href`, which a modified or middle click follows out of the app
 * without ever reaching an onClick handler.
 *
 * Resolution is delegated to `URL` rather than a hand-rolled pattern so the check
 * is what the browser would actually do. That folds `\` to `/` and strips leading
 * whitespace and tabs, so `\\evil.com`, `/\evil.com`, `//evil.com`, `javascript:`
 * and `' //evil.com'` all fall out as off-origin for free.
 *
 * Origin-only by design: a same-origin absolute URL is allowed even though
 * react-router resolves it as a relative segment. External navigation is
 * unaffected — it goes through `window.location` / `window.open`, never here.
 *
 * @param {string|number|object} to - v6 navigation target (path, delta, or partial location).
 * @returns {boolean} False for anything that would leave the current origin, for any path
 * containing a backslash, and when the document origin is opaque (fails closed).
 */
export const isSafeNavigationTarget = (to) => {
    // A history delta (-1, 1) never carries a destination.
    if (typeof to === 'number') return true
    const path = typeof to === 'string' ? to : to?.pathname
    // A search/hash-only update leaves the path untouched.
    if (path === undefined || path === null || path === '') return true
    if (typeof path !== 'string') return false
    // Refuse every backslash, including ones `URL` reads as same-origin
    // (`/workflows\@evil.com`): belt-and-braces against a URL parser that does not
    // fold `\` while some layer below it does.
    if (path.includes('\\')) return false
    try {
        return new URL(path, window.location.origin).origin === window.location.origin
    } catch {
        // Unparseable target, or an opaque document origin (a sandboxed iframe without
        // allow-same-origin, where `location.origin` is the string 'null'). Fail closed.
        return false
    }
}

/**
 * Report and refuse an unsafe navigation target. Refusals are otherwise invisible in
 * production — react-router would have thrown — so they also reach `window.Logger`.
 *
 * @param {string|number|object} to - Navigation target.
 * @returns {boolean} True when the target was REFUSED, so callers read as
 * `if (rejectUnsafeTarget(to)) return`.
 */
const rejectUnsafeTarget = (to) => {
    if (isSafeNavigationTarget(to)) return false
    console.warn('[router-compat] refused unsafe navigation target', to)
    window.Logger?.log?.('Router > Refused unsafe navigation target', String(to))
    return true
}

/**
 * Run a v6-shaped raw navigate while temporarily disabling blocker consultation.
 * Used both internally by `useBlockableNavigate` (when a blocker explicitly
 * resolves to "allow") and by the navigation object handed to a blocker handler
 * — the handler often calls `navigation.push(...)` to redirect somewhere else
 * after blocking, which must not re-trigger itself.
 *
 * @param {Function} rawNavigate - The raw v6 navigate.
 * @param {string|object|number} to - Destination.
 * @param {object} [options] - v6 navigate options.
 */
const navigateBypass = (rawNavigate, to, options) => {
    // The single funnel for every raw navigate reachable from a blocker handler
    // (navigation.push/replace/go/goBack/goForward/resume). Guarding here as well as in
    // `useBlockableNavigate` is what makes "nothing routes around the check" true.
    if (rejectUnsafeTarget(to)) return
    isBypassingBlockers = true
    try {
        rawNavigate(to, options)
    } finally {
        isBypassingBlockers = false
    }
}

/**
 * Compute a v5-shaped target location from a v6 navigate call. Used to populate
 * `navigation.pausedLocation()` and the `location` arg passed to blocker handlers.
 *
 * For numeric `to` (back/forward), v6 doesn't expose the destination before the
 * navigation completes. We special-case `to === -1` by using the
 * `historySingleton.previousLocation` mirror that `useNotifyLocationListeners`
 * keeps in sync with the Redux router slice — that recovers v5 behavior for the
 * most common back-navigation case (Topbar's go-back, etc.) so handlers like
 * ConnectionBuilder's discard prompt see the real destination instead of the
 * current pathname (which makes pathname-equality guards short-circuit).
 *
 * Other numeric values (`-2`, `1`, ...) still fall back to the current location.
 *
 * @param {string|object|number} to - Navigate destination.
 * @param {object} options - Navigate options.
 * @param {object} currentLocation - Fallback when `to` is numeric (back/forward).
 * @returns {object} `{ pathname, search, hash, state }`.
 */
const computeTarget = (to, options, currentLocation) => {
    if (typeof to === 'number') {
        if (to === -1 && historySingleton.previousLocation) {
            const prev = historySingleton.previousLocation
            return {
                pathname: prev.pathname || (currentLocation?.pathname ?? '/'),
                search: prev.search || '',
                hash: prev.hash || '',
                state: prev.state
            }
        }
        return currentLocation
    }
    if (typeof to === 'string') {
        try {
            const url = new URL(to, 'http://_placeholder_')
            return {
                pathname: url.pathname || currentLocation?.pathname || '/',
                search: url.search || '',
                hash: url.hash || '',
                state: options?.state
            }
        } catch {
            return { pathname: to, search: '', hash: '', state: options?.state }
        }
    }
    if (to && typeof to === 'object') {
        return {
            pathname: to.pathname || currentLocation?.pathname || '/',
            search: to.search || '',
            hash: to.hash || '',
            state: to.state ?? options?.state
        }
    }
    return currentLocation
}

/**
 * Build the `navigation` object passed as the first argument to a blocker handler.
 * Mirrors the @allpro/react-router-pause shape so existing handler code keeps working:
 *   - `pausedLocation()` returns the target location.
 *   - `push/replace/go/goBack/goForward` perform a real navigation that bypasses
 *     the blocker (so handlers can redirect to a different URL after blocking).
 *   - `resume()` lets the original navigation proceed.
 *   - `cancel()` is a no-op (user stays on the current page; same as returning false).
 *
 * @param {Function} rawNavigate - Raw v6 navigate.
 * @param {object} target - v5-shaped target location.
 * @param {string} action - 'PUSH'|'REPLACE'|'POP'.
 * @returns {object} navigation control object.
 */
const buildNavigationObject = (rawNavigate, target, action) => ({
    isPaused: true,
    pausedLocation: () => target,
    pausedAction: () => action,
    push: (path, state) => navigateBypass(rawNavigate, path, state ? { state } : undefined),
    replace: (path, state) => navigateBypass(rawNavigate, path, { replace: true, ...(state ? { state } : {}) }),
    go: (n) => navigateBypass(rawNavigate, n),
    goBack: () => navigateBypass(rawNavigate, -1),
    goForward: () => navigateBypass(rawNavigate, 1),
    cancel: () => undefined,
    resume: () => {
        const url = `${target.pathname || ''}${target.search || ''}${target.hash || ''}`
        navigateBypass(rawNavigate, url || target.pathname, {
            replace: action === 'REPLACE',
            ...(target.state !== undefined ? { state: target.state } : {})
        })
    }
})

// --- Infinite navigation-loop circuit breaker (safety net + diagnostic) ---
// A blocker that returns `false` for navigation after navigation, with no
// allowed navigation in between, signals a navigate -> block -> re-dispatch ->
// navigate loop. It happens when an app flow re-issues a navigation faster than
// React can re-render and clear the blocker's `when` (e.g. ConnectionBuilder's
// post-save redirect: history.navigate -> blocked + dispatch(verifyChangedConn)
// -> onCheckChanges -> onDiscard -> history.navigate -> ...). `setVerifyRouter(false)`
// inside the handler can't break it because `whenRef` only updates on the next
// React render, which never gets to run. The synchronous variant overflows the
// stack (a visible RangeError, fixed in SG-14091 via isConsultingBlocker); the
// saga-driven variant escapes that guard and instead pegs the main thread,
// FREEZING the tab with no console output and climbing memory.
//
// When more than MAX_BLOCKS_PER_WINDOW navigations are blocked within
// LOOP_WINDOW_MS, log the offending target + blocker once and force the
// navigation through (bypassing the blocker) so the app recovers instead of
// hanging. No legitimate interaction blocks dozens of navigations per second.
const LOOP_WINDOW_MS = 1000
const MAX_BLOCKS_PER_WINDOW = 40
let blockWindowStart = 0
let blockWindowCount = 0
// Once a loop is detected we LATCH into bypass mode: the navigate path stops
// consulting the blocker entirely. Consulting calls the handler, and it is the
// handler (dispatch -> saga -> navigate) that re-issues the navigation and
// sustains the loop — so merely forcing each navigation through is not enough,
// we must stop calling the handler. The latch clears once navigation settles
// (no active blocker) or a navigation is allowed through normally.
let blockLoopLatched = false

/**
 * Record a blocked navigation and report whether a navigation loop is underway.
 * Latches `blockLoopLatched` (logging once) when the per-window block count
 * crosses the threshold.
 *
 * @param {string} targetKey - pathname+search of the blocked target.
 * @param {string} label - The active blocker's label.
 * @returns {boolean} True once a loop has been latched.
 */
const registerBlockAndDetectLoop = (targetKey, label) => {
    const now = Date.now()
    if (now - blockWindowStart > LOOP_WINDOW_MS) {
        blockWindowStart = now
        blockWindowCount = 0
    }
    blockWindowCount += 1
    if (!blockLoopLatched && blockWindowCount >= MAX_BLOCKS_PER_WINDOW) {
        blockLoopLatched = true
        console.error(
            `[router-compat] blocker "${label}" blocked ${blockWindowCount} navigations in ${LOOP_WINDOW_MS}ms ` +
            `(latest target "${targetKey}"). Suspected infinite navigation loop — bypassing the blocker to recover.`,
            new Error('navigation-loop-trace').stack
        )
    }
    return blockLoopLatched
}

/**
 * Clear the navigation-loop counter and latch after navigation settles.
 */
const resetBlockWindow = () => {
    blockWindowStart = 0
    blockWindowCount = 0
    blockLoopLatched = false
}

/**
 * Wrap v6 `useNavigate()` with blocker consultation. Returns a stable navigate
 * function that:
 *   1. If no blockers are registered, behaves like the raw v6 navigate.
 *   2. Otherwise calls the most recently registered blocker's handler with
 *      `(navigation, location, action)`.
 *      - Handler returns `false` → navigation is blocked.
 *      - Anything else (`true`, `undefined`, …) → navigation proceeds.
 *
 * Note on the `undefined` semantics: @allpro/react-router-pause used to treat
 * undefined as "pause" — handler would later call `navigation.resume()` /
 * `.push(...)` / `.cancel()` to resolve the pause. None of this codebase's
 * handlers use that pattern; they all return `false` to block and fall
 * through to `undefined` on cases like "destination is the same page" (e.g.
 * `history.navigate({ search }, { replace: true })` to clear a query param).
 * Treating `undefined` as block silently swallows those same-page updates —
 * so we collapse the contract to "false blocks, anything else allows".
 *
 * Use as the source of truth for `history.navigate` so all in-app navigation
 * passes through the blocker chain.
 *
 * @returns {Function} Wrapped navigate.
 */
export const useBlockableNavigate = () => {
    const rawNavigate = useNavigate()
    const location = useLocation()
    const locationRef = useRef(location)
    locationRef.current = location
    return useMemo(() => (to, options) => {
        // Refuse off-origin targets before any blocker logic runs, so neither
        // `bypassBlockers` nor a latched loop can skip the check. Handler-issued
        // navigations are covered by the same guard inside `navigateBypass`.
        if (rejectUnsafeTarget(to)) return undefined
        const blocker = findActiveBlocker()
        // `options.bypassBlockers` is an explicit opt-out for flows that have
        // already decided to leave (e.g. the builder's post-discard/post-save
        // redirect in `onDiscard`). Without it, a blocker whose `when` hasn't
        // been cleared yet re-blocks the redirect and re-dispatches, looping.
        // react-router's navigate ignores the unknown option, so it's safe to
        // pass through.
        if (isBypassingBlockers || isConsultingBlocker || options?.bypassBlockers || !blocker) {
            // Nothing is blocking (or an explicit opt-out) — navigation has
            // settled, so clear any latched loop state.
            blockLoopLatched = false
            return rawNavigate(to, options)
        }
        // A latched loop means we must NOT consult the blocker: the handler is
        // what re-dispatches and sustains the loop. Let navigations through
        // without calling it until navigation settles.
        if (blockLoopLatched) {
            return rawNavigate(to, options)
        }
        const action = typeof to === 'number' ? 'POP' : (options?.replace ? 'REPLACE' : 'PUSH')
        const target = computeTarget(to, options, locationRef.current)
        const navigation = buildNavigationObject(rawNavigate, target, action)
        const result = consultBlocker(blocker, navigation, target, action)
        if (result === false) {
            const targetKey = `${target?.pathname || ''}${target?.search || ''}`
            // Stop consulting the blocker once it is stuck re-blocking a
            // re-dispatched navigation (navigate -> block -> dispatch -> navigate),
            // which otherwise freezes the tab. See the circuit-breaker note above.
            if (registerBlockAndDetectLoop(targetKey, blocker.label)) {
                return rawNavigate(to, options)
            }
            return undefined
        }
        resetBlockWindow()
        return navigateBypass(rawNavigate, to, options)
    }, [rawNavigate])
}

/**
 * Mirrors react-router's internal `shouldProcessLinkClick`: only a plain
 * left-click, without modifier keys, on a same-tab target is handled as an SPA
 * navigation. Modified/middle clicks and `target="_blank"` fall through to the
 * browser (open-in-new-tab, etc.) instead of being intercepted.
 *
 * @param {object} event - The click event.
 * @param {string} [target] - The anchor `target` attribute.
 * @returns {boolean} True when the click should drive an in-app navigation.
 */
const shouldHandleLinkClick = (event, target) =>
    event.button === 0 &&
    (!target || target === '_self') &&
    !(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey)

/**
 * Blocker-aware `<Link>`. Renders react-router's `<Link>` (so the anchor, href,
 * and accessibility behavior are unchanged) but performs the actual navigation
 * through `useBlockableNavigate()` so the click passes through any active
 * `<ReactRouterPause>` blocker. React-router's own `<Link>` navigates via the
 * router's internal navigator, which has no `history.block()` equivalent under
 * `<BrowserRouter>` and would therefore bypass the blocker registry entirely.
 *
 * We let our `onClick` run first and `preventDefault()` it: react-router's Link
 * only calls its internal navigate when the event was not default-prevented, so
 * navigation happens exactly once — via the blockable path.
 *
 * @returns {React.ReactElement} A blocker-aware link element.
 */
export const Link = forwardRef(({ onClick, to, replace, state, target, ...rest }, ref) => {
    const navigate = useBlockableNavigate()
    // SG-14529 — sanitise the `to` we hand to react-router, not just the click.
    // react-router renders an off-origin `to` as a real href, and a modified or
    // middle click is deliberately left to the browser by `shouldHandleLinkClick`,
    // so it would leave the app without `handleClick` ever running.
    const safeTo = isSafeNavigationTarget(to) ? to : ''
    const handleClick = (event) => {
        if (onClick) onClick(event)
        if (event.defaultPrevented || !shouldHandleLinkClick(event, target)) return
        event.preventDefault()
        if (rejectUnsafeTarget(to)) return
        navigate(to, { replace: !!replace, ...(state !== undefined ? { state } : {}) })
    }
    return <RouterLink ref={ref} to={safeTo} target={target} onClick={handleClick} {...rest} />
})
Link.displayName = 'Link'

/**
 * Blocker-aware `<NavLink>`. Same interception as {@link Link}, but renders
 * react-router's `<NavLink>` so active-state styling (`className`/`style`
 * callbacks, `isActive`) keeps working off the current location.
 *
 * @returns {React.ReactElement} A blocker-aware nav link element.
 */
export const NavLink = forwardRef(({ onClick, to, replace, state, target, ...rest }, ref) => {
    const navigate = useBlockableNavigate()
    // SG-14529 — sanitise the `to` we hand to react-router, not just the click.
    // react-router renders an off-origin `to` as a real href, and a modified or
    // middle click is deliberately left to the browser by `shouldHandleLinkClick`,
    // so it would leave the app without `handleClick` ever running.
    const safeTo = isSafeNavigationTarget(to) ? to : ''
    const handleClick = (event) => {
        if (onClick) onClick(event)
        if (event.defaultPrevented || !shouldHandleLinkClick(event, target)) return
        event.preventDefault()
        if (rejectUnsafeTarget(to)) return
        navigate(to, { replace: !!replace, ...(state !== undefined ? { state } : {}) })
    }
    return <RouterNavLink ref={ref} to={safeTo} target={target} onClick={handleClick} {...rest} />
})
NavLink.displayName = 'NavLink'

/**
 * v5-shaped `useHistory()` shim built on top of v6 hooks. Returns an object
 * exposing `push`, `replace`, `goBack`, `goForward`, `go`, `location`, plus
 * `listen` and a no-op `block`. The returned object identity is stable across
 * renders (matching v5's singleton `history`), so `useEffect`/`useMemo` deps
 * containing `history` won't refire on every navigation. `location` reads via
 * a getter so destructuring still yields the current value.
 *
 * Routes all navigation through the blocker chain so `<ReactRouterPause>` can
 * intercept `useHistory().push(...)`-driven navigation the same as
 * `history.navigate(...)`. Migrate consumers to `useNavigate()` over time and
 * remove the dependency on this shim.
 *
 * @returns {object} v5-shaped history object with stable identity.
 */
export const useHistory = () => {
    const navigate = useBlockableNavigate()
    const location = useLocation()
    const navigateRef = useRef(navigate)
    const locationRef = useRef(location)
    navigateRef.current = navigate
    locationRef.current = location
    return useMemo(() => buildStableHistoryShim(navigateRef, locationRef), [])
}

/**
 * Percent-decode a pathname one segment at a time, exactly as react-router does
 * internally before matching (`decodePath`, re-escaping any `/` a segment decodes
 * to so it cannot invent a new segment).
 *
 * react-router's public `matchPath` does NOT decode: it hands back whatever the raw
 * pathname held, so `/stories/SADE%20Telecom` yields `storyId === 'SADE%20Telecom'`.
 * Its own hooks compensate — `useMatch` calls `matchPath(pattern, decodePath(pathname))`
 * and `matchRoutes` decodes before matching — which is why `useParams()` returns decoded
 * values everywhere else in the app. `useRouteMatch` below matched raw, so any route
 * param carrying an encoded character (a space, an accent) came back encoded and every
 * lookup against it missed. Same in react-router 6 and 7; this was never version-specific.
 *
 * @param {string} value - Raw pathname, e.g. `location.pathname`.
 * @returns {string} The decoded pathname, or the input unchanged if it is malformed.
 */
const decodePath = (value) => {
    try {
        return value.split('/').map(v => decodeURIComponent(v).replace(/\//g, '%2F')).join('/')
    } catch {
        // A bad percent-encoding ("%zz"): match on the raw value rather than throwing.
        return value
    }
}

/**
 * v5-shaped `useRouteMatch()` shim. Accepts a string path, an array of paths, or an
 * options object `{ path, exact, strict, sensitive }`. Returns `{ params, path, url, isExact }`
 * or `null`. Multi-path matching is supported via `matchPath` calls (no hooks-in-loop).
 *
 * @param {string|Array|object} input - Path, array of paths, or options object.
 * @returns {object|null} v5-shaped match.
 */
export const useRouteMatch = (input) => {
    const location = useLocation()
    return useMemo(() => {
        const pathname = decodePath(location?.pathname || '/')
        if (input === undefined || input === null) {
            return { params: {}, path: pathname, url: pathname, isExact: true }
        }
        let paths
        let options = {}
        if (typeof input === 'string') {
            paths = [input]
        } else if (Array.isArray(input)) {
            paths = input
        } else {
            options = input
            paths = Array.isArray(input.path) ? input.path : [input.path]
        }
        const found = paths
            .flatMap(p => expandV5Path(p))
            .map(p => v6MatchPath({ path: p, end: !!options.exact, caseSensitive: !!options.sensitive }, pathname))
            .find(Boolean)
        if (!found) return null
        return {
            params: found.params,
            path: found.pattern.path,
            url: found.pathname,
            isExact: found.pathname === pathname
        }
    }, [input, location?.pathname])
}

/**
 * v5-shaped `withRouter` HOC shim. Injects `history`, `location`, and `match` props
 * mirroring the v5 prop contract. Consumers reading `this.props.history.push(...)` keep working.
 *
 * @param {React.ComponentType} Component - Class or function component.
 * @returns {React.ComponentType} Wrapped component.
 */
export const withRouter = (Component) => {
    const Wrapped = (props) => {
        // Use the blockable navigate so `this.props.history.push(...)` from class
        // components / HOC consumers also routes through any active <ReactRouterPause>.
        const navigate = useBlockableNavigate()
        const location = useLocation()
        const params = useParams()
        const historyProp = useMemo(() => buildHistoryShim(navigate, location), [navigate, location])
        const match = useMemo(() => ({
            params,
            path: location.pathname,
            url: location.pathname,
            isExact: true
        }), [params, location.pathname])
        return <Component {...props} history={historyProp} location={location} match={match} />
    }
    Wrapped.displayName = `withRouter(${Component.displayName || Component.name || 'Component'})`
    return Wrapped
}

/**
 * Memo-stable form of a v5 `to` argument. v5 code commonly does
 * `<Redirect to={{ pathname: '/x' }} />` (or with `state: { from: location }`),
 * which re-creates the `to` object on every render. v6 `<Navigate>`'s effect
 * deps are by reference, so a fresh object refires the navigate on every render
 * — a tight loop with the LOCATION_CHANGE-driven re-renders we wire up.
 *
 * v6 also separates `state` from `to` (it's a sibling prop on `<Navigate>`),
 * so we return a tuple of v6-safe destination + state, both reference-stable
 * when the meaningful parts haven't changed.
 *
 * @param {string|object} to - The v5 `to` value.
 * @returns {{ v6To: string|object, state: any }} v6-safe destination + state.
 */
const useStableNavigateTo = (to) => {
    const isObj = to && typeof to === 'object'
    const pathname = isObj ? to.pathname || '' : (typeof to === 'string' ? to : '')
    const search = isObj ? to.search || '' : ''
    const hash = isObj ? to.hash || '' : ''
    const state = isObj ? to.state : undefined
    const fromPathname = state && state.from ? state.from.pathname || '' : ''
    const fromSearch = state && state.from ? state.from.search || '' : ''
    return useMemo(() => {
        if (typeof to === 'string') return { v6To: to, state: undefined }
        if (!isObj) return { v6To: to, state: undefined }
        const v6To = (search || hash) ? { pathname, search, hash } : pathname
        return { v6To, state: state ?? undefined }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [typeof to === 'string', pathname, search, hash, fromPathname, fromSearch])
}

/**
 * v5-shaped `<Redirect>` element. Renders a v6 `<Navigate>` when used as a leaf JSX node.
 * When used inside a v5 `<Switch>`, the Switch shim detects the Redirect type and emits
 * a `<Route element={<Navigate ... />} path={from} />` instead.
 *
 * v5's `to={{ pathname, state: { from } }}` shape passed `state` inside `to`. v6 reads
 * `state` from a separate `<Navigate state={...} />` prop, so we extract it here.
 *
 * @param {object} props - Props.
 * @param {string|object} props.to - Destination path or location object.
 * @param {boolean} [props.push] - If true, a push navigation is performed (default replace).
 * @returns {React.ReactNode} Navigate element.
 */
export const Redirect = ({ to, push }) => {
    const { v6To, state } = useStableNavigateTo(to)
    return <Navigate to={v6To} replace={!push} state={state} />
}

/**
 * Defers a v5-style `render` prop until the matching v6 Route actually mounts.
 * Eagerly calling `render({})` in `transformSwitchChild` would re-evaluate every
 * route's render fn on every Switch render — creating fresh `<Redirect>`/element
 * objects whose refs change each pass, causing v6 `<Navigate>` to fire its effect
 * in a loop. We also reconstruct the v5 render-prop argument shape
 * (`{ match, location, history }`) so callbacks like
 * `({ location }) => <Redirect to={{ state: { from: location } }} />` keep working.
 *
 * @param {object} props - Props.
 * @param {Function} props.render - The v5 render function.
 * @returns {React.ReactNode} The element returned by the render fn.
 */
const RenderRouteElement = ({ render }) => {
    const location = useLocation()
    // Use the blockable navigate so v5 render-prop consumers reading
    // `history.push(...)` from the render arg also pass through active blockers.
    const navigate = useBlockableNavigate()
    const params = useParams()
    const match = useMemo(() => ({
        params,
        path: location.pathname,
        url: location.pathname,
        isExact: true
    }), [params, location.pathname])
    const historyArg = useMemo(() => buildHistoryShim(navigate, location), [navigate, location])
    return render({ match, location, history: historyArg }) || null
}

/**
 * Walks one Switch child and decides how to fold it into the v6 Routes tree.
 * Returns `{ routes: [<V6Route .../>...], passthrough: ReactNode|null }` —
 * routes get added to the v6 `<Routes>`, passthrough is collected and
 * eventually rendered as the *element* of a wildcard fallback Route
 * (`<Route path="*" element={<>{passthrough}</>} />`). That preserves v5's
 * first-match semantics where bare children only render if no earlier
 * Route/Redirect matched.
 *
 * @param {React.ReactElement} child - Direct child of `<Switch>`.
 * @returns {{routes: React.ReactNode[], passthrough: React.ReactNode}} Folded result.
 */
const transformSwitchChild = (child) => {
    if (!React.isValidElement(child)) return { routes: [], passthrough: child }
    if (child.type === React.Fragment) {
        // Flatten fragments so their children are evaluated individually.
        return React.Children.toArray(child.props.children).reduce((acc, c) => {
            const { routes, passthrough } = transformSwitchChild(c)
            return {
                routes: acc.routes.concat(routes),
                passthrough: passthrough != null
                    ? (acc.passthrough != null ? <>{acc.passthrough}{passthrough}</> : passthrough)
                    : acc.passthrough
            }
        }, { routes: [], passthrough: null })
    }
    if (child.type === Redirect) {
        // Re-use the Redirect shim itself so the `to` value goes through
        // `useStableNavigateTo` and we don't get a fresh Navigate every render.
        return {
            routes: [<V6Route key={`__redirect_${child.props.from || '*'}`} path={child.props.from || '*'} element={<Redirect {...child.props} />} />],
            passthrough: null
        }
    }
    const childProps = child.props || {}
    const { component: Component, render, children: routeChildren, exact, strict, sensitive, path, element, ...rest } = childProps
    // A child without a `path` (and that isn't a Redirect/Route) is something
    // v5 Switch would render only if no preceding Route matched. We mark it as
    // passthrough; <Switch> wraps the collected passthrough into a single
    // wildcard `<Route path="*" />` so it stays a fallback (v5 first-match
    // behavior) instead of rendering alongside whatever Route matched (v6's
    // default for siblings of <Routes>).
    if (path === undefined && Component === undefined && render === undefined && element === undefined) {
        return { routes: [], passthrough: child }
    }
    let nextElement = element
    if (nextElement === undefined) {
        if (Component) nextElement = <Component />
        else if (typeof render === 'function') nextElement = <RenderRouteElement render={render} />
        else if (routeChildren) nextElement = typeof routeChildren === 'function'
            ? <RenderRouteElement render={routeChildren} />
            : routeChildren
        else nextElement = null
    }
    // Expand v5-style array paths (`path={['/a', '/b']}`) and `:foo*` / `:foo?`
    // segments into v6-safe paths, then ensure non-exact routes end with `/*` so
    // v6's strict end-matching keeps the v5 "starts-with" semantics — and so any
    // descendant `<Routes>` rendered by `nextElement` doesn't trip the v6 warning
    // "parent route path has no trailing '*'".
    const initialPaths = Array.isArray(path) ? path : [path]
    const expandedPaths = initialPaths.flatMap(p => expandV5Path(p))
    const finalPaths = exact ? expandedPaths : expandedPaths.map(ensureSplatTail)
    const dedupedPaths = [...new Set(finalPaths)]
    return {
        routes: dedupedPaths.map(p => (
            <V6Route key={p} path={p} element={nextElement} {...rest} />
        )),
        passthrough: null
    }
}

/**
 * v5 Routes default to "starts-with" matching (no `exact` prop). v6 Routes
 * default to end-matching, so to keep v5 semantics we append `/*` — also a
 * requirement for any parent Route whose `element` renders descendant `<Routes>`.
 *
 * @param {string} path - Path pattern.
 * @returns {string} Path with a trailing `/*` (idempotent).
 */
const ensureSplatTail = (path) => {
    if (typeof path !== 'string') return path
    if (path === '*' || path === '/*') return path
    if (path.endsWith('/*')) return path
    if (path === '/') return '/*'
    return `${path}/*`
}

/**
 * v5-shaped `<Switch>` shim. Splits children into Route-like and non-Route
 * elements: Routes become entries in a v6 `<Routes>` block, and non-Route
 * children get folded into a single wildcard `<Route path="*" />` so they
 * only render when no earlier path-bearing route matched — preserving v5's
 * "first match wins, non-route children act as a default fallback" semantics.
 *
 * Without this, v5 patterns like
 *   `<Switch><Route path="/a" /><WebsocketProvider>...</WebsocketProvider></Switch>`
 * would render the provider tree alongside `/a` (the v6 default) instead of
 * only when `/a` didn't match (the v5 default).
 *
 * @param {object} props - Props.
 * @param {React.ReactNode} props.children - v5 Route / Redirect / non-Route elements.
 * @returns {React.ReactNode} A v6 `<Routes>` block.
 */
export const Switch = ({ children }) => {
    const folded = React.Children.toArray(children).reduce((acc, c) => {
        const { routes, passthrough } = transformSwitchChild(c)
        return {
            routes: acc.routes.concat(routes),
            passthrough: passthrough != null
                ? (acc.passthrough != null ? <>{acc.passthrough}{passthrough}</> : passthrough)
                : acc.passthrough
        }
    }, { routes: [], passthrough: null })
    // Non-Route children become a single wildcard fallback. v6's Routes uses
    // path-specificity ordering, so `path="*"` is always last to match — which
    // matches v5's "fall through to bare children when nothing else won".
    // Skip the synthetic fallback if the caller already provided an explicit
    // root wildcard (`*` or `/*`) — their route wins, matching v5 semantics.
    const hasExplicitWildcard = folded.routes.some(r => r?.props?.path === '*' || r?.props?.path === '/*')
    const routes = folded.passthrough != null && !hasExplicitWildcard
        ? folded.routes.concat(
            <V6Route key="__shim_fallback__" path="*" element={folded.passthrough} />
        )
        : folded.routes
    return <Routes>{routes}</Routes>
}

/**
 * `matchPath` v5-compatible signature. v5 accepted `(pathname, options)`; v6 accepts
 * `({ path, end, caseSensitive }, pathname)`. This shim normalizes both call shapes.
 *
 * @param {string|object} pathnameOrOptions - Pathname (v5) or options (v6).
 * @param {string|object} optionsOrPathname - Options (v5) or pathname (v6).
 * @returns {object|null} Match result.
 */
export const matchPath = (pathnameOrOptions, optionsOrPathname) => {
    if (typeof pathnameOrOptions === 'string') {
        const pathname = pathnameOrOptions
        const opts = optionsOrPathname
        if (!opts) return null
        let paths
        let options = {}
        if (typeof opts === 'string') {
            paths = [opts]
        } else if (Array.isArray(opts)) {
            paths = opts
        } else {
            options = opts
            paths = Array.isArray(opts.path) ? opts.path : [opts.path]
        }
        const found = paths
            .flatMap(p => expandV5Path(p))
            .map(p => v6MatchPath({ path: p, end: !!options.exact, caseSensitive: !!options.sensitive }, pathname))
            .find(Boolean)
        if (!found) return null
        return {
            params: found.params,
            path: found.pattern.path,
            url: found.pathname,
            isExact: found.pathname === pathname
        }
    }
    const opts = pathnameOrOptions || {}
    const candidates = expandV5Path(opts.path)
    return candidates
        .map(p => v6MatchPath({ ...opts, path: p }, optionsOrPathname))
        .find(Boolean) || null
}

/**
 * Hook reading the previous router location from the Redux `router` slice
 * (maintained by the reducer in `src/redux/reducers/Router/index.js`).
 * Replaces `react-router-last-location`'s `useLastLocation` hook.
 *
 * @returns {object|null} The previous `{ pathname, search }` or `null`.
 */
export const useLastLocation = () => useSelector(state => state.router.previousLocation)

/**
 * HOC injecting `lastLocation` prop. Replaces `react-router-last-location`'s
 * `withLastLocation`.
 *
 * @param {React.ComponentType} Component - Component to wrap.
 * @returns {React.ComponentType} Wrapped component.
 */
export const withLastLocation = (Component) => {
    const Wrapped = (props) => {
        const lastLocation = useLastLocation()
        return <Component {...props} lastLocation={lastLocation} />
    }
    Wrapped.displayName = `withLastLocation(${Component.displayName || Component.name || 'Component'})`
    return Wrapped
}

/**
 * No-op provider — previous-location tracking is handled by the router reducer now.
 * Kept as a drop-in replacement so callers that wrap their tree with `<LastLocationProvider>`
 * can switch the import path without further changes.
 *
 * @param {object} props - Props.
 * @param {React.ReactNode} props.children - Children.
 * @returns {React.ReactNode} Children unchanged.
 */
export const LastLocationProvider = ({ children }) => children

/**
 * v6-compatible re-implementation of `@allpro/react-router-pause`. Registers a
 * navigation blocker while `when` is truthy. The blocker is consulted by the
 * blockable navigate returned from `useBlockableNavigate()` (used by both
 * `history.navigate(...)` via `NavigationBinder` and by `useHistory()`).
 *
 * Handler signature: `(navigation, location, action) => boolean | undefined`.
 *   - `navigation` exposes `pausedLocation()` and `push/replace/go/goBack/goForward`
 *     for redirecting elsewhere from the handler.
 *   - `location` is the v5-shaped target location.
 *   - `action` is `'PUSH' | 'REPLACE' | 'POP'`.
 *   - Return `false` to block. Anything else (`true`, `undefined`) allows the
 *     navigation. Implicit `undefined` returns from same-pathname guards
 *     (e.g. `if (pathname !== nextPathName) return false`) therefore allow
 *     same-page query/state updates rather than swallowing them.
 *
 * Document-level navigations (page reload, tab close, typing a new URL) are
 * blocked via a `beforeunload` listener that triggers the browser's native
 * "leave site?" confirm dialog while `when` is truthy. The browser does not
 * let us provide a custom UI for this — the native dialog is the only option
 * v6 exposes without migrating to `createBrowserRouter`.
 *
 * Browser back/forward (POP) is intercepted via a capture-phase popstate
 * listener that runs before react-router's bubble-phase handler. When the
 * blocker handler returns false, we restore the URL via `history.pushState`
 * and call `stopImmediatePropagation()` so react-router never sees the event
 * — the user stays on the page and the handler can dispatch its own confirm
 * UI (e.g. ConnectionBuilder's discard dialog). Anything else (true,
 * undefined) lets the popstate fall through and react-router processes it
 * normally — same "false blocks, everything else allows" contract as
 * `useBlockableNavigate`.
 *
 * The "trap URL" we restore to follows the live `useLocation()` value, so
 * an in-app replace/push that happens *while* the blocker is active (e.g.
 * SourceFileForm clearing a one-shot `?reload=…` search param) is reflected
 * — a subsequent blocked back doesn't reintroduce the stale query state.
 *
 * @param {object} props - Props.
 * @param {boolean} props.when - When truthy, the blocker is active.
 * @param {Function} props.handler - Navigation attempt handler.
 * @returns {null} Renders nothing.
 */
export const ReactRouterPause = ({ when, handler, label }) => {
    // Keep the latest handler in a ref so re-renders that close over fresh
    // state (typical for React function components) don't require re-registering.
    const handlerRef = useRef(handler)
    handlerRef.current = handler
    // Keep the latest `when` in a ref so the (mount-once) registration can read
    // the live value via `isActive()` without re-running its effect. Tying the
    // effect to `when` made registration flap with every data-driven toggle,
    // opening windows where a navigation slipped through unblocked.
    const whenRef = useRef(when)
    whenRef.current = when
    // Track the "trap" URL we'll restore to if the handler blocks a popstate.
    // It needs to follow in-app replaces/pushes that happen *while* the blocker
    // is active (e.g. SourceFileForm clearing a one-shot search param via
    // `history.navigate({ search }, { replace: true })`). Using a ref synced
    // from `useLocation()` keeps it in step without reattaching the popstate
    // listener every render.
    const location = useLocation()
    const trapURLRef = useRef('')
    const trapStateRef = useRef(null)
    trapURLRef.current = `${location.pathname}${location.search}${location.hash}`
    trapStateRef.current = window.history.state
    const blockerLabel = label || handler?.name || 'anonymous'
    useEffect(() => {
        // Register ONCE for the component's lifetime. Whether this blocker wants
        // to block right now is decided per-navigation via `isActive()` reading
        // the live `when` (through `whenRef`) — see `findActiveBlocker`. This
        // keeps registration stable across `when` churn so a transient flip
        // can't open an unblocked window.
        const entry = {
            label: blockerLabel,
            isActive: () => Boolean(whenRef.current),
            handler: (...args) => handlerRef.current && handlerRef.current(...args)
        }
        blockerRegistry.push(entry)
        // Block tab-close / reload / URL-bar navigation. The listener is attached
        // for the blocker's lifetime but only triggers the native dialog while
        // `when` is truthy, so unrelated pages never see it. Modern browsers
        // ignore the returned/assigned message and show their own dialog; setting
        // returnValue is the documented way to opt in.
        const onBeforeUnload = (event) => {
            if (!whenRef.current) return undefined
            event.preventDefault()
            // Some older browsers still read `returnValue` for the prompt text.
            event.returnValue = ''
            return ''
        }
        const onPopState = (event) => {
            // Only the active top-of-stack blocker handles each popstate; if
            // there are nested blockers, the others early-return and let the
            // last-registered active one decide.
            if (findActiveBlocker() !== entry) return
            const target = {
                pathname: window.location.pathname,
                search: window.location.search,
                hash: window.location.hash,
                state: window.history.state || null
            }
            const navigation = buildNavigationObject(historySingleton.navigate, target, 'POP')
            const result = consultBlocker(entry, navigation, target, 'POP')
            // Only an explicit `false` blocks. Anything else (true, undefined)
            // lets the popstate fall through to react-router — see
            // `useBlockableNavigate` for why we collapsed the v5 pause contract.
            if (result !== false) return
            event.stopImmediatePropagation()
            // Read from the live refs so a same-blocker replace/push doesn't
            // restore us to a stale URL.
            window.history.pushState(trapStateRef.current, '', trapURLRef.current)
        }
        window.addEventListener('beforeunload', onBeforeUnload)
        window.addEventListener('popstate', onPopState, true)
        return () => {
            const idx = blockerRegistry.indexOf(entry)
            if (idx >= 0) blockerRegistry.splice(idx, 1)
            window.removeEventListener('beforeunload', onBeforeUnload)
            window.removeEventListener('popstate', onPopState, true)
        }
    // Register once on mount; activity is gated by `whenRef`, not by re-running.
    }, [])
    return null
}

/**
 * Test-only helper to clear the blocker registry between tests. Public-named
 * but only intended for use from `pythagora_tests`.
 */
export const clearBlockersForTests = () => {
    blockerRegistry.length = 0
    isBypassingBlockers = false
    isConsultingBlocker = false
    resetBlockWindow()
}

/**
 * Hook used by NavigationBinder to fan location changes out to listeners registered
 * via `history.listen()`. Mounted once at the root so any consumer's `history.listen`
 * subscription receives every navigation.
 *
 * Also keeps two singleton fields fresh for non-React callers:
 *   - `historySingleton.location`: the current location.
 *   - `historySingleton.previousLocation`: the location we navigated FROM. The
 *      previous value is captured into a ref *before* the singleton is updated so
 *      this stays correct regardless of effect ordering with other consumers
 *      (e.g. NavigationBinder's useLayoutEffect that also writes location).
 *      Used by `computeTarget` to give blocker handlers a meaningful target for
 *      `navigate(-1)` / `history.goBack()` (v6 doesn't expose the destination
 *      for numeric POP navigations directly).
 */
export const useNotifyLocationListeners = () => {
    const location = useLocation()
    const lastLocationRef = useRef(null)
    useEffect(() => {
        historySingleton.previousLocation = lastLocationRef.current
        historySingleton.location = location
        lastLocationRef.current = location
        locationListeners.forEach(l => {
            try { l(location, 'POP') } catch { /* swallow */ }
        })
    }, [location])
}
